import { sizeOf, type ServerSize } from './server.js';

// Billing (M11): what a server costs while it's awake, and to create. Prices live here, one
// place both the API (which saves the price on each usage row) and the web app (which shows it)
// read. Changing a price is a PR; rows already written keep the price they started with.

/**
 * Money in ten-thousandths of a dollar ($0.0825 = 825), so sums of many small amounts stay exact
 * (whole numbers, no floating-point drift). Shown rounded to the cent (`formatMoney`).
 */
export type Money = number;
export const MONEY_PER_DOLLAR = 10_000;

/**
 * AWS's on-demand Linux price an hour in us-west-2 (the only region yet), by instance type: the
 * sizes' types and the ones performance tests use. To check against AWS's own (the Pricing API)
 * before changing prices.
 */
export const AWS_HOURLY: Readonly<Record<string, Money>> = {
  't4g.medium': 336,
  't4g.large': 672,
  'c7g.medium': 363,
  'c7g.large': 725,
  'c7g.xlarge': 1450,
  'm7g.large': 816,
  'm7g.xlarge': 1632,
  'c8g.large': 798,
  'c8g.xlarge': 1595,
  'm8g.large': 898,
};

/** What Hearth adds to AWS's price, an hour, on an instance type outside the sizes (an admin's test type). */
export const MARGIN_PER_HOUR: Money = 100;

/**
 * What each size costs an hour awake (owner, 2026-10-26, from the simulation perf run): round
 * numbers that cover AWS's price, data sent to players (~$0.009 a player-hour, worst case) and card fees.
 */
export const SIZE_PRICE_PER_HOUR: Readonly<Record<ServerSize, Money>> = {
  small: 1300,
  medium: 1900,
  large: 2900,
};

/** Charged once when a server is created. */
export const CREATE_FEE: Money = 50_000;

/** Charged each time a server's size changes, up or down (owner, 2026-10-27: so sizes don't change on a whim). */
export const RESIZE_FEE: Money = 5_000;

/** The first month (UTC, YYYY-MM) with usage recorded (M11): no month before it is asked for. */
export const USAGE_SINCE = '2026-10';

/** What an hour awake on this instance type costs (AWS's price plus the margin); undefined for a type without a price (logged, not billed). */
export function pricePerHour(instanceType: string): Money | undefined {
  const aws = AWS_HOURLY[instanceType];
  return aws === undefined ? undefined : aws + MARGIN_PER_HOUR;
}

/** The hourly price of a server as it is now: its own instance type's (an admin's), else its size's. */
export const serverPricePerHour = (server: { instanceType?: string; size?: ServerSize }): Money | undefined =>
  server.instanceType === undefined ? sizePricePerHour(sizeOf(server)) : pricePerHour(server.instanceType);

/** A size's hourly price, for the create page and settings. */
export const sizePricePerHour = (size: ServerSize): Money => SIZE_PRICE_PER_HOUR[size];

/** What `seconds` awake cost at `perHour`, to the ten-thousandth of a dollar. */
export const awakeCost = (perHour: Money, seconds: number): Money => Math.round((perHour * Math.max(0, seconds)) / 3600);

/** "$4.82", "$0.08": rounded to the cent. */
export function formatMoney(amount: Money): string {
  const cents = Math.round(amount / 100);
  return `$${Math.floor(cents / 100)}.${String(cents % 100).padStart(2, '0')}`;
}

/** GSI on `Usage`: `ownerId` + `at`, for one person's usage across their servers (their bill). */
export const USAGE_BY_OWNER_INDEX = 'byOwner';

/**
 * An item in the `Usage` table, keyed by `serverId` + `at`: one per time a server was awake
 * (`awake`, from its instance's running event to its stopping one), or a fee (`create`, `resize`).
 */
export interface UsageRecord {
  serverId: string;
  /** When the run started (the instance's running event), or the fee was charged. */
  at: string;
  kind: 'awake' | 'create' | 'resize';
  /** Who pays: the server's owner when the row was written. */
  ownerId: string;
  /** awake: the instance that ran, its type and the server's size then. */
  instanceId?: string;
  instanceType?: string;
  size?: ServerSize;
  /** awake: the price an hour when it started (absent: a type without a price, not billed). */
  pricePerHour?: Money;
  /** awake: who woke it (a user's sub), when known. */
  wokenBy?: string;
  /** awake: when it stopped running (absent while it's awake). */
  stoppedAt?: string;
  /** create, resize: the fee. */
  amount?: Money;
  /** resize: the size before and after, and who changed it (a user's sub, or an admin's). */
  fromSize?: ServerSize;
  toSize?: ServerSize;
  by?: string;
}

/** A month in UTC as `YYYY-MM`. */
export type Month = string;

/** The first moment of a month (UTC) and of the next, in milliseconds: `start` is in it, `end` is not. */
export function monthBounds(month: Month): { start: number; end: number } {
  const [year, mon] = month.split('-').map(Number) as [number, number];
  return { start: Date.UTC(year, mon - 1, 1), end: Date.UTC(year, mon, 1) };
}

/** The month before the one `now` is in (UTC): what the monthly invoice on the 1st covers. */
export function previousMonth(now: Date): Month {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)).toISOString().slice(0, 7);
}

/**
 * The Hearth user's billing standing (M11 billing): absent is fine. `past_due` means an invoice
 * failed to charge: they can't wake servers or create new ones until it's paid (running ones
 * are never stopped for it).
 */
export type BillingStatus = 'past_due';

/** Where one owner's invoice for one month stands. */
export type InvoiceState =
  /** Written before Stripe is called: a job that died here is retried (Stripe's idempotency key makes that safe). */
  | 'claimed'
  /** Stripe has the invoice (`stripeInvoiceId`) and will charge the saved card. */
  | 'sent'
  | 'paid'
  | 'failed';

/** An item in the `Billing` table, keyed by `userId` + `month`: one invoice a person gets a month. */
export interface BillingRecord {
  userId: string;
  month: Month;
  state: InvoiceState;
  /** The whole invoice, in whole cents (what Stripe charges). */
  cents: number;
  createdAt: string; // ISO 8601 UTC
  stripeInvoiceId?: string;
  /** When Stripe last reported it paid, or failed to charge. */
  paidAt?: string;
  failedAt?: string;
}
